<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import kanbanClient from 'dashboard/api/kanban/kanbanClient';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({ deal: { type: Object, required: true } });
const { t } = useI18n();
const { accountId } = useAccount();
const { run, isPending } = useAbortableRequest();
const movements = ref([]);
const page = ref(0);
const hasMore = ref(false);
const error = ref(false);

const load = async () => {
  error.value = false;
  movements.value = [];
  hasMore.value = false;
  try {
    await run(async signal => {
      const { data } = await kanbanClient.getDealStageMovements(
        props.deal.kanban_funnel_id,
        props.deal.id,
        page.value,
        { signal }
      );
      if (signal.aborted) return;
      movements.value = data.movements;
      hasMore.value = data.has_more;
    });
  } catch {
    error.value = true;
  }
};
watch(
  [accountId, () => props.deal.id, () => props.deal.kanban_stage_id],
  () => {
    page.value = 0;
    load();
  },
  { immediate: true }
);
const changePage = value => {
  page.value += value;
  load();
};
</script>

<template>
  <section class="flex flex-col gap-2 border-t border-n-weak pt-3">
    <div class="flex items-center justify-between gap-2">
      <h3 class="m-0 text-sm font-semibold">
        {{ t('KANBAN.TIMELINE.TITLE') }}
      </h3>
      <Button
        variant="ghost"
        size="sm"
        icon="i-lucide-refresh-cw"
        :disabled="isPending"
        :aria-label="t('KANBAN.TIMELINE.REFRESH')"
        @click="load"
      />
    </div>
    <p class="m-0 text-xs text-n-slate-10">{{ t('KANBAN.TIMELINE.LIMIT') }}</p>
    <Spinner v-if="isPending" />
    <p v-else-if="error" role="alert" class="text-xs text-n-ruby-11">
      {{ t('KANBAN.TIMELINE.ERROR') }}
    </p>
    <ol v-else class="m-0 flex list-none flex-col p-0">
      <li
        v-for="movement in movements"
        :key="movement.id"
        class="border-s border-n-weak pb-4 ps-3 text-xs last:pb-0"
      >
        <p class="m-0 font-medium">
          {{
            t('KANBAN.TIMELINE.TRANSITION', {
              from: movement.from_stage_name,
              to: movement.to_stage_name,
            })
          }}
        </p>
        <p
          v-if="movement.source === 'api' || movement.source === 'paid_order'"
          class="m-0 mt-1 font-medium"
          :class="
            movement.source === 'paid_order'
              ? 'text-n-teal-11'
              : 'text-n-slate-11'
          "
        >
          {{
            movement.source === 'paid_order'
              ? t('KANBAN.TIMELINE.SOURCES.paid_order')
              : t('KANBAN.TIMELINE.SOURCES.api')
          }}
        </p>
        <time :datetime="movement.occurred_at" class="text-n-slate-10">
          {{ new Date(movement.occurred_at).toLocaleString() }}
        </time>
        <p class="m-0 text-n-slate-10">
          <template v-if="movement.actor?.name">
            {{
              t('KANBAN.TIMELINE.NAMED_ACTOR', { name: movement.actor.name })
            }}
          </template>
          <template v-else-if="movement.actor_id">
            {{ t('KANBAN.TIMELINE.ACTOR', { id: movement.actor_id }) }}
          </template>
          <template v-else>
            {{ t('KANBAN.TIMELINE.UNKNOWN_ACTOR') }}
          </template>
        </p>
      </li>
      <li v-if="!movements.length" class="text-xs text-n-slate-10">
        {{ t('KANBAN.TIMELINE.EMPTY') }}
      </li>
    </ol>
    <div v-if="page || hasMore" class="flex justify-end gap-2">
      <Button
        variant="ghost"
        :disabled="!page || isPending"
        :label="t('KANBAN.TASKS.PREVIOUS')"
        @click="changePage(-1)"
      />
      <Button
        variant="ghost"
        :disabled="!hasMore || isPending"
        :label="t('KANBAN.TASKS.NEXT')"
        @click="changePage(1)"
      />
    </div>
  </section>
</template>
